"use client"

import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import { forwardRef, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/autoplay"
import "swiper/css/effect-fade"
import "swiper/css/navigation"
import { Autoplay, EffectFade, Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { useInViewConfig, useIsDesktop } from "ui"
import ButtonWithArrow from "ui/common/bit/ButtonWithArrow"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import { mediaQueries } from "ui/theme/mediaQueries"
import { colors, sizes } from "ui/theme/theme"
import { extractMenuProperties, getNonUmbracoUrl } from "utils"
import { sendBlockEvent } from "utils/gtm"
import BlockWrapper from "../common/BlockWrapper"
import CommonTitle from "../common/bit/CommonTitle"

const CARD_HEIGHT = 244

const ForwardedBlockWrapper = forwardRef<HTMLDivElement, any>((props, ref) => (
  <BlockWrapper {...props} innerRef={ref} />
))

const StyledContainer = styled(ForwardedBlockWrapper)`
  width: 100%;
  z-index: 4;

  ${mediaQueries.mobile} {
    padding-inline-start: 0;
    padding-inline-end: 0;
    padding-block-end: 0;
    gap: 45px;
    flex-direction: column-reverse;
    align-items: flex-start;
  }
  ${mediaQueries.tablet} {
    padding-inline-start: 0;
    padding-inline-end: 0;
    padding-block-end: 0;
    gap: 45px;
    flex-direction: column-reverse;
    align-items: flex-start;
  }

  ${mediaQueries.desktopM} {
    padding-top: 0;
    height: 596px;
    gap: 60px;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  ${mediaQueries.desktopXl} {
    height: 800px;
    padding-inline-end: 60px;
  }
`

const StyledImageContainer = styled(Box)`
  position: absolute;
  z-index: -1;

  ${mediaQueries.mobile} {
    width: 100%;
    height: 50%;
    bottom: -21px;
    max-width: 100%;
  }

  ${mediaQueries.tablet} {
    height: 60%;
    left: 0;
  }

  ${mediaQueries.desktopM} {
    width: 800px;
    height: 100%;
  }
  ${mediaQueries.desktopXl} {
  }
`

const StyledSwiperContainer = styled(Box)`
  position: relative;
  max-width: 1000px;
  height: 100%;

  ${mediaQueries.mobile} {
    display: none;
  }
  ${mediaQueries.tablet} {
    display: none;
  }
  ${mediaQueries.desktopM} {
    display: flex;
  }
`

const StyledSwiper = styled(Swiper, {
  shouldForwardProp: (prop) => !["$isMobile"].includes(prop),
})<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    padding-inline-start: 24px;
    padding-inline-end: 24px;
    
    ${mediaQueries.mobile} {
      padding-top: 0;
    }
    ${mediaQueries.tablet} {
      padding-top: 0;
      margin-inline-end: 0;
    }
    ${mediaQueries.desktopM} {
      display: none;
    }
  `
      : `
    width: 100%;
    height: 100%;
    
    .swiper-slide {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `}
`

const StyledSwiperSlide = styled(SwiperSlide, {
  shouldForwardProp: (prop) => !["$isMobile"].includes(prop),
})<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-radius: 30px;
    width: 100%;
    height: 340px;
    padding: 30px;
    gap: 12px;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: -8px 8px 30px 0px rgba(2, 1, 64, 0.08);
    backdrop-filter: blur(8px);
    a {
      color: ${colors.black};
    }
  `
      : `
    // Desktop styles handled by DesktopItemContent
  `}
`

const StyledNavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s ease;

  &.swiper-button-prev {
    left: -60px;
    transform: translateY(-50%) rotate(180deg);
  }

  &.swiper-button-next {
    right: -60px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover {
    transform: translateY(-50%) scale(1.1);

    &.swiper-button-prev {
      transform: translateY(-50%) rotate(180deg) scale(1.1);
    }
  }

  svg {
    width: 48px;
    height: 48px;
    transition: all 0.3s ease;
  }
`

const StyledTextsContainer = styled(Box)`
  margin-inline-start: ${sizes.baseDouble};
  margin-inline-end: ${sizes.baseDouble};
  width: 100%;
  ${mediaQueries.desktopM} {
    margin-inline-start: 0;
  }
`
const StyledCommonTitle = styled(CommonTitle)`
  align-items: flex-start;
  ${mediaQueries.mobile} {
    h2 {
      text-align: start;
    }
  }
  ${mediaQueries.desktopM} {
    h2 {
      text-align: start;
    }
  }
`

const DesktopItemContent = styled(Box)`
  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${sizes.baseTriple};
  padding: ${sizes.baseQuadric};
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: -8px 8px 30px 0px rgba(2, 1, 64, 0.08);
  backdrop-filter: blur(8px);
  a {
    color: ${colors.black};
  }
  ${mediaQueries.desktopM} {
    width: 510px;
    padding: ${sizes.baseQuadric};
    height: ${CARD_HEIGHT}px;
  }

  ${mediaQueries.desktopXl} {
    width: 640px;
    height: 340px;
  }
`

const StyledIamgeContainer = styled(Box)`
  position: relative;
  display: flex;
  height: 130px;
  width: 121px;
  flex: 0 0 auto;
  ${mediaQueries.desktopM} {
    height: 100%;
    width: 167px;
  }
  ${mediaQueries.desktopXl} {
    width: 186px;
  }
  img {
    object-fit: contain;
  }
`
const StyledTypography = styled(Typography)<{ component?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.baseHalf};
`

const StyledNumber = styled("span")`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: ${colors.red};
  color: ${colors.white};
  border-radius: 30px;
  height: 40px;
  width: 40px;
  padding: 12px 16px;
  font-family: "Brown Trial Heb";
  font-size: 16px;
  box-shadow: -8px 8px 30px rgba(15, 36, 79, 0.12);
  margin-inline-end: ${sizes.baseSingle};
  ${mediaQueries.mobile} {
    position: absolute;
    top: 14px;
    left: 14px;
  }
`

const StyledButtonsContainer = styled(Box)`
  display: flex;
  gap: 30px;
  margin-top: 30px;
  ${mediaQueries.mobile} {
    gap: 10px;
    margin-inline-start: -${sizes.baseSingle};
  }
`
const StyledBullets = styled(Box)`
  color: ${colors.black};
  font-size: 18px;
  p {
    margin: 0;
  }
  ul {
    padding-inline-start: 18px;
    gap: 12px;
    li {
      text-align: start;
      font-weight: 400;
    }
  }
`

const StyledButtonWithArrow = styled(ButtonWithArrow)`
  ${mediaQueries.mobile} {
    p {
      width: 100px;
      white-space: normal;
      height: auto;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }
  }
`

const SlideContent = ({
  card,
  index,
  isMobile,
}: {
  card: any
  index: number
  isMobile: boolean
}) => {
  const currTitle = extractMenuProperties(card).title
  const currBullets = extractMenuProperties(card).bulets
  const currImage = extractMenuProperties(card).image

  const imageContent = currImage && (
    <StyledIamgeContainer>
      {isLottieFile(currImage) ? (
        <LottiePlayer
          lottieUrl={currImage}
          width={"100%"}
          height={"100%"}
          playOnce={true}
          autoplay={true}
          loop={false}
          ariaLabel={currTitle}
        />
      ) : (
        <Image src={currImage} alt={currTitle} fill />
      )}
    </StyledIamgeContainer>
  )

  const titleContent = currTitle && (
    <StyledTypography variant="h4" component={isMobile ? "h4" : "p"}>
      {!isMobile && <StyledNumber>{index + 1}</StyledNumber>}
      {currTitle}
    </StyledTypography>
  )

  const bulletsContent = currBullets && (
    <StyledBullets
      dangerouslySetInnerHTML={{
        __html: replaceRichTextLinks(currBullets),
      }}
    />
  )

  if (isMobile) {
    return (
      <>
        <StyledNumber>{index + 1}</StyledNumber>
        {imageContent}
        {titleContent}
        {bulletsContent}
      </>
    )
  }

  // Desktop layout
  return (
    <DesktopItemContent>
      {imageContent}
      <Box>
        {titleContent}
        {bulletsContent}
      </Box>
    </DesktopItemContent>
  )
}

const Claim = ({ id, data }: { data: any; id?: string }) => {
  const { ref, inView } = useInView(useInViewConfig)
  const [mounted, setMounted] = useState(false)
  const isDesktop = useIsDesktop()
  const { title, cardsList, subtitle, text, button1, button2 } = data

  useEffect(() => {
    setMounted(true)
  }, [])

  // Combined swiper configuration
  const getSwiperProps = (isMobile: boolean) => {
    if (isMobile) {
      return {
        slidesPerView: 1.2,
        spaceBetween: 20,
      }
    }

    return {
      modules: [EffectFade, Autoplay, Navigation],
      effect: "fade" as const,
      fadeEffect: {
        crossFade: true,
      },
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      loop: true,
      slidesPerView: 1,
      speed: 1200,
    }
  }

  const renderSwiper = (isMobile: boolean) => (
    <StyledSwiper
      $isMobile={mounted ? isMobile : undefined}
      {...getSwiperProps(isMobile)}
    >
      {cardsList?.blocks?.map((card: any, i: number) => (
        <StyledSwiperSlide key={i} $isMobile={mounted ? isMobile : undefined}>
          <SlideContent card={card} index={i} isMobile={isMobile} />
        </StyledSwiperSlide>
      ))}
    </StyledSwiper>
  )

  // Don't render until mounted to avoid hydration mismatch
  if (!mounted) {
    return <div style={{ minHeight: "596px" }} />
  }

  return (
    <StyledContainer id={id}>
      <div ref={ref} style={{ height: "100%" }}>
        <StyledImageContainer>
          <Image
            sizes="100vw"
            fill
            src={
              isDesktop
                ? "/hachsharaProtectClaim.svg"
                : "/hachsharaProtectClaimMobile.svg"
            }
            alt={title?.value}
          />
        </StyledImageContainer>
        {/* DESKTOP */}
        <StyledSwiperContainer>{renderSwiper(false)}</StyledSwiperContainer>
      </div>
      {/* MOBILE */}
      {renderSwiper(true)}
      <StyledTextsContainer>
        <StyledCommonTitle
          title={title?.value}
          subtitle={subtitle?.value}
          text={text?.value}
        />
        <StyledButtonsContainer>
          {button1?.links[0] && (
            <StyledButtonWithArrow
              onClick={() => {
                sendBlockEvent({
                  blockId: id || "6",
                  componentName: "הגשת תביעה",
                  title: title?.value,
                  buttonName: button1?.links[0]?.name,
                  productName: data?.formDefaultInsuranceType,
                })
              }}
              href={getNonUmbracoUrl(button1?.links[0]?.url)}
            >
              <Typography variant="body2">{button1?.links[0]?.name}</Typography>
            </StyledButtonWithArrow>
          )}
          {button2?.links[0] && (
            <StyledButtonWithArrow
              variant="elevated"
              colorPallette={"blue"}
              onClick={() => {
                sendBlockEvent({
                  blockId: id || "6",
                  componentName: "הגשת תביעה",
                  title: title?.value,
                  buttonName: button2?.links[0]?.name,
                  productName: data?.formDefaultInsuranceType,
                })
              }}
              href={getNonUmbracoUrl(button2?.links[0]?.url)}
            >
              <Typography variant="body2">{button2?.links[0]?.name}</Typography>
            </StyledButtonWithArrow>
          )}
        </StyledButtonsContainer>
      </StyledTextsContainer>
    </StyledContainer>
  )
}

export default Claim
